
import { useCallback } from 'react';
import yaml from 'js-yaml';

/**
 * useTournamentIO
 * 
 * Handles file input/output operations for tournament data (YAML).
 */
export const useTournamentIO = ({
  tournamentName,
  tournamentDetails,
  columns,
  rows,
  events,
  setInitialData,
  setIsReadyEditing,
  tournamentId,
  showNotification,
  t
}) => {

  const handleFileSave = useCallback(async () => {
    const dataToSave = {
      name: tournamentName,
      details: tournamentDetails,
      structure: { columns, levels: rows, events },
    };
    const yamlStr = yaml.dump(dataToSave);
    const filename = `${tournamentName || 'tournament'}.yaml`;

    try {
      if (window.showSaveFilePicker) {
        const options = {
          suggestedName: filename,
          types: [{
            description: 'YAML files',
            accept: { 'text/yaml': ['.yaml', '.yml'] },
          }],
        };
        const handle = await window.showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(yamlStr);
        await writable.close();
      } else {
        const blob = new Blob([yamlStr], { type: 'text/yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      showNotification(t('controller.fileSavedSuccessfully'), 'success');
    } catch (err) {
      if (err.name !== 'AbortError') {
        showNotification(t('errors.saveError'), 'error');
      }
    }
  }, [tournamentName, tournamentDetails, columns, rows, events, showNotification, t]);

  const handleFileLoad = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = yaml.load(event.target.result);
        if (data && typeof data === 'object') {
          const newInitialData = {
            // Keep the current tournament ID if it exists, otherwise use the one from file or generate new
            id: tournamentId || data.id || Date.now().toString(),
            name: data.name || '',
            details: data.details || {},
            structure: data.structure || { columns: [], levels: [], events: [] },
            timer_state: data.timer_state, // Do not default here, let hook handle it
          };
          setInitialData(newInitialData);
          setIsReadyEditing(true);
          showNotification(t('controller.fileLoadedSuccessfully'), 'success');
        } else {
          showNotification(t('errors.controller.invalidFileFormat'), 'error');
        }
      } catch (error) {
        showNotification(t('errors.controller.fileParseFailed'), 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [setInitialData, setIsReadyEditing, showNotification, t, tournamentId]);

  return {
    handleFileSave,
    handleFileLoad
  };
};
